import dayjs from 'dayjs';
import { getToken } from '../store/auth';

/** 从对象中取第一个存在的字段（兼容 camelCase / snake_case） */
export function field<T = any>(obj: any, ...names: string[]): T | undefined {
  if (!obj) return undefined;
  for (const n of names) {
    if (obj[n] !== undefined && obj[n] !== null) return obj[n] as T;
  }
  return undefined;
}

/** 给 Lychee 智能客服 相对路径附加 JWT（<audio> 等原生请求无法带 Authorization 头，走 query token，同 WS 模式） */
export function authUrl(url: string): string {
  const token = getToken();
  if (!url || !token) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}token=${encodeURIComponent(token)}`;
}

export function fmtTime(v: any, format = 'YYYY-MM-DD HH:mm:ss'): string {
  if (!v) return '-';
  const d = dayjs(v);
  return d.isValid() ? d.format(format) : String(v);
}

/**
 * 绝对时刻（展示与导出的**唯一绝对值出口**）：`fmtTime` 的语义不变，
 * 名字显式化，供「相对时间」档下的悬浮提示与导出/CSV 路径引用，
 * 避免"展示口径改了、导出跟着变成『3 分钟前』"这类串味。
 */
export function fmtTimeAbs(v: any, format = 'YYYY-MM-DD HH:mm:ss'): string {
  return fmtTime(v, format);
}

/**
 * 距今多久（相对时间档的展示形态）：
 * 1 分钟内「刚刚」· 1 小时内「N 分钟前」· 当天「今天 HH:mm」· 昨天「昨天 HH:mm」·
 * 一周内「N 天前」· 更早回落到绝对日期；未来时刻按同一分档对称给「N 分钟后 / 今天 HH:mm / 明天 HH:mm」。
 * 相对口径以**本地时区**的自然日为准（同"昨天"的直觉一致），不引入时长阈值近似。
 */
export function fmtTimeRel(v: any, now: Date = new Date()): string {
  if (!v) return '-';
  const d = dayjs(v);
  if (!d.isValid()) return String(v);
  const t = d.toDate();
  const diffMs = now.getTime() - t.getTime();
  const future = diffMs < 0;
  const absSec = Math.abs(Math.floor(diffMs / 1000));
  const hm = d.format('HH:mm');
  const dayDiff = dayjs(now).startOf('day').diff(d.startOf('day'), 'day');
  if (absSec < 60) return future ? '马上' : '刚刚';
  if (absSec < 3600) return future ? `${Math.floor(absSec / 60)} 分钟后` : `${Math.floor(absSec / 60)} 分钟前`;
  if (dayDiff === 0) return `今天 ${hm}`;
  if (dayDiff === 1) return `昨天 ${hm}`;
  if (dayDiff === -1) return `明天 ${hm}`;
  if (dayDiff > 1 && dayDiff <= 7) return `${dayDiff} 天前`;
  if (dayDiff < -1 && dayDiff >= -7) return `${-dayDiff} 天后`;
  return d.format('YYYY-MM-DD');
}

/** 毫秒 → 整秒 mm:ss（粗粒度展示出口，如报表时长；转写起止请用 fmtSpan） */
export function fmtMs(ms: any): string {
  const total = Math.max(0, Math.floor(Number(ms) / 1000) || 0);
  const m = String(Math.floor(total / 60)).padStart(2, '0');
  const s = String(total % 60).padStart(2, '0');
  return `${m}:${s}`;
}

/**
 * 起止时间「视为同一时刻」的阈值（毫秒）：系统里多处用 end_ms = start_ms + 1 表示「无时长」
 * （满意度按键评分、无时间轴兜底等），秒级显示下与真正同时刻无区别，按同一时刻只显示一遍。
 */
const SPAN_SAME_EPS_MS = 10;

/**
 * 转写/日志「说话的起止」渲染出口（毫秒级 mm:ss.SSS）：
 * 转写是复盘口播节奏的证据，秒级会把「抢话重叠」与「正常轮次」显示成同一时刻，
 * 故固定 3 位小数，便于对照录音核对句间真实间隙。
 * 起止相差 ≤ SPAN_SAME_EPS_MS 视为同一时刻只显示一遍（在线会话/按键等无时长场景），
 * 否则显示 `起 – 止`；单侧缺失按单时间显示，两侧都无有效值返回 ''（调用方据此不渲染）。
 * 时间口径：录音相对毫秒（相对接通时刻），与 transcript_segment 的 start_ms/end_ms 同轴。
 */
export function fmtSpan(startMs: any, endMs: any, sep = ' – '): string {
  const ok = (v: any) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v)) && Number(v) >= 0;
  const s = ok(startMs) ? Number(startMs) : null;
  const e = ok(endMs) ? Number(endMs) : null;
  if (s === null && e === null) return '';
  if (s === null || e === null) return fmtMsDetail(s === null ? e : s);
  return Math.abs(e - s) <= SPAN_SAME_EPS_MS ? fmtMsDetail(s) : `${fmtMsDetail(s)}${sep}${fmtMsDetail(e)}`;
}

/** 毫秒 → mm:ss.SSS（固定 3 位小数）：转写起止（fmtSpan）与 AI 调用日志细粒度时间轴的渲染出口 */
export function fmtMsDetail(ms: any): string {
  const n = Math.max(0, Number(ms) || 0);
  const total = Math.floor(n / 1000);
  const m = String(Math.floor(total / 60)).padStart(2, '0');
  const s = String(total % 60).padStart(2, '0');
  const r = String(n % 1000).padStart(3, '0');
  return `${m}:${s}.${r}`;
}

/** 毫秒 → 紧凑相对耗时：<1s 显示纯 ms，≥1s 显示带秒，≥60s 回落 mm:ss.SSS（AI 调用日志延迟/步长用） */
export function fmtLatency(ms: any): string {
  const n = Math.max(0, Number(ms) || 0);
  if (n < 1000) return `${n}ms`;
  const s = n / 1000;
  if (s < 60) return `${s.toFixed(3)}s`;
  return fmtMsDetail(n);
}

/**
 * 秒 → 「1时20分3秒」文案。
 * **分工**：非列场景（StatCard 大数字、Descriptions 明细、图表 valueFormatter、拼接文案）直接用它；
 * **表格列一律走 `components/DurationText`**——列需要"空值/0 显示 `-`"这套三态，
 * 且 0 是否有意义由页面决定（`showZero`），字符串函数表达不了。
 */
export function fmtDuration(sec: any): string {
  const s = Math.floor(Number(sec) || 0);
  if (s <= 0) return '0秒';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return `${h}时${m}分${r}秒`;
  if (m > 0) return `${m}分${r}秒`;
  return `${r}秒`;
}

/** 归一化分页响应 { list, total }，兼容直接返回数组 */
export function ensureList(data: any): { list: any[]; total: number } {
  if (Array.isArray(data)) return { list: data, total: data.length };
  if (data && Array.isArray(data.list)) {
    return { list: data.list, total: Number(data.total) || data.list.length };
  }
  return { list: [], total: 0 };
}

/** 逗号/换行分隔字符串 -> 数组 */
export function splitList(v: string): string[] {
  return (v || '')
    .split(/[\n,，、;；]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * 长文本拆分展示（报告证据/建议等共用）。
 * 先按换行分段；段内优先按句末标点拆，无句末标点再依次按分号、逗号兜底；仍无分隔符则整段保留。
 * `softSplit=false`（默认 true）时只按换行与句末标点拆，不再按分号/逗号兜底：
 * 证据需引用对话原文，按逗号硬拆会把一句原文切成几行。
 */
export function splitSentences(text?: string, opts?: { softSplit?: boolean }): string[] {
  const soft = opts?.softSplit !== false;
  const t = String(text ?? '').replace(/\r\n/g, '\n').trim();
  if (!t) return [];
  const out: string[] = [];
  for (const para of t.split(/\n+/)) {
    const p = para.trim();
    if (!p) continue;
    const sentences = p.split(/(?<=[。！？!?])/).map((s) => s.trim()).filter(Boolean);
    if (sentences.length > 1) {
      out.push(...sentences);
      continue;
    }
    if (!soft) {
      out.push(p);
      continue;
    }
    const semis = p.split(/(?<=[；;])/).map((s) => s.trim()).filter(Boolean);
    if (semis.length > 1) {
      out.push(...semis);
      continue;
    }
    const commas = p.split(/(?<=[，,])/).map((s) => s.trim()).filter(Boolean);
    out.push(...(commas.length > 1 ? commas : [p]));
  }
  return out.length > 0 ? out : [t];
}

/** 数组或逗号分隔字符串 -> 展示字符串 */
export function joinList(v: any): string {
  if (Array.isArray(v)) return v.join(', ');
  return v ? String(v) : '-';
}

/**
 * 字符串数组 JSON -> 字符串数组。
 * 兼容三种来源：①后端 JSON 字符串（Ticket.Tags 等 `json.Marshal` 列）；
 * ②已被 axios/preprocess 拆好的数组；③非法/空值兜底空数组。
 * 不抛错（用于详情渲染容错，坏数据不应阻断详情打开）。
 */
export function parseJsonArray(v: any): string[] {
  if (Array.isArray(v)) return v.filter((x) => typeof x === 'string');
  if (!v || typeof v !== 'string') return [];
  try {
    const parsed = JSON.parse(v);
    if (Array.isArray(parsed)) return parsed.filter((x) => typeof x === 'string');
  } catch {
    /* fallthrough */
  }
  return [];
}

/** 数字千分位（报表/驾驶舱大数字统一展示） */
export function fmtNumber(v: any): string {
  const n = Number(v);
  if (v === undefined || v === null || Number.isNaN(n)) return '-';
  return n.toLocaleString('en-US');
}

export const PHONE_REG = /^1[3-9]\d{9}$|^0\d{2,3}-?\d{7,8}$|^\+?\d{5,15}$/;

/** 号码脱敏（展示兜底）：保留前 3 后 4，中间 ****；非 7~20 位数字串原样返回。 */
export function maskPhone(s: any): string {
  const v = String(s ?? '').trim();
  if (!v) return '';
  const digits = (v.match(/\d/g) || []).length;
  if (digits < 7 || digits > 20) return v;
  const head = 3;
  const tail = 4;
  const n = v.length;
  if (n <= head + tail) return v.slice(0, Math.ceil(n / 2)) + '*'.repeat(n - Math.ceil(n / 2));
  let out = '';
  for (let i = 0; i < n; i++) {
    if (i >= head && i < n - tail && /\d/.test(v[i])) out += '*';
    else out += v[i];
  }
  return out;
}
